import React, { useState } from "react";
import {
  Box,
  Button,
  Center,
  Container,
  Divider,
  Flex,
  Paper,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { checkoutSubscription } from "../api";
import mixpanel from "../tracking";
import HeroFaq from "./FAQ";
import { Link } from "wouter";

function checkout(plan, callback) {
  checkoutSubscription(plan)
    .then((data) => {
      console.debug("redirecting to: " + data.redirectUrl, data);
      mixpanel.track("Checkout Plan", {
        plan: plan.planName,
        interval: plan.interval,
      });
      if (callback) callback();
      location.href = data.redirectUrl;
    })
    .catch((error) => {
      mixpanel.track("Checkout Plan FAIL", {
        plan: plan.planName,
        interval: plan.interval,
      });
      alert("Sorry, something went wrong: " + error);
      if (callback) callback();
    });
}

function PlanBox({ plan, highlight, interval, style }) {
  const theme = useMantineTheme();
  const [isLoading, setIsLoading] = useState(false);
  // const [location, setLocation] = useLocation();

  const baseStyle = {
    boxShadow: "0px 30px 50px -7px rgba(0,0,0,0.1)",
    padding: "1.5rem",
    backgroundColor: theme.colors.dark[5],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  const highlightStyle = {
    ...baseStyle,
    padding: "4.0rem 2.0rem",
    background:
      "linear-gradient(to bottom right, hsl(190, 15%, 45%), hsl(220, 10%, 10%))",
    color: "white",
    borderColor: "white",
    borderWidth: "1px",
    borderRadius: "0.7rem",
    border: "2px solid #eaeafe",
  };

  return (
    <Box
      miw={{ base: "100%", sm: highlight ? "19rem" : "17rem" }}
      mih={highlight ? "27rem" : "22rem"}
      style={highlight ? highlightStyle : { ...baseStyle, ...style }}
    >
      <Stack w="100%" align="center" spacing="xs">
        <Text
          style={{
            fontWeight: 700,
          }}
          fz={"md"}
        >
          {plan.title}
        </Text>

        {interval == "1" && (
          <Title
            order={3}
            c="dimmed"
            style={{
              fontSize: 24,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
            td="line-through"
          >
            <Text fz={"2rem"} td="line-through">
              $
            </Text>
            {plan.price[0]}
          </Title>
        )}

        <Title
          order={2}
          fz={{ base: "2.25rem", sm: "3rem" }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          <Text fz={"2rem"}>$</Text>
          {plan.price[interval]}
        </Title>

        <Text fz="sm" c="dimmed">
          Per month, billed {interval == 0 ? "monthly" : "annually"}
        </Text>

        <Stack
          w={"100%"}
          align="center"
          spacing={10}
          style={{
            color: theme.colorScheme === "light" && "hsl(350, 65%, 45%)",
          }}
        >
          {plan.features.map((item) => (
            <React.Fragment key={item.id}>
              <Divider
                style={{
                  width: "100%",
                  borderColor: theme.colorScheme === "dark" && "gray",
                  opacity: theme.colorScheme === "dark" && 0.7,
                }}
              />
              <Text fz={"sm"} fw={600}>
                {item.text}
              </Text>
            </React.Fragment>
          ))}
        </Stack>
        <Button
          loading={isLoading}
          // variant="white"
          //variant="gradient"
          variant={highlight ? "gradient" : "filled"}
          gradient={
            highlight
              ? { from: "grape", to: "pink.5", angle: 45 }
              : { from: "indigo", to: "lightBlue", angle: 45 }
          }
          size="lg"
          onClick={() => {
            setIsLoading(true);
            checkout({ planName: plan.id, interval }, () =>
              setIsLoading(false)
            );
          }}
          fullWidth
        >
          Subscribe
        </Button>
      </Stack>
    </Box>
  );
}

export const PlanPricingPage = () => {
  // const theme = useMantineTheme();
  const [interval] = useState("0");

  const plan1 = {
    title: "Basic",
    id: "Basic",
    features: [
      { id: 1, text: "5,000 credits per month" },
      { id: 2, text: "One custom model per moth" },
    ],
    price: [19.99, 13.83],
  };

  const plan2 = {
    title: "Pro",
    id: "Pro",
    features: [
      { id: 10, text: "12,500 credits per month" },
      { id: 11, text: "3 custom models per month" },
    ],
    price: [39.99, 33.25],
  };

  const plan3 = {
    title: "Premium",
    id: "Premium",
    features: [
      { id: 10, text: "37,500 credits per month" },
      { id: 11, text: "7 custom models per month" },
    ],
    price: [99.99, 83.08],
  };

  return (
    <Container size="lg">
      <Stack spacing={40} mt="md" mb="xl" pt="md" pb="xl">
        {/** header section */}

        <a id="start" />
        <Flex
          direction="column"
          gap={10}
          align="center"
          justify="start"
          mb="xl"
        >
          <Title>Plans</Title>
          <Text size="lg" ta="center" fw={500}>
            Choose the best plan for you and start creating beautiful images.
          </Text>
          <Text ta="center" c="dimmed">
            Accepting all major credit cards. Cancel any time.
          </Text>
        </Flex>
        {/* <Flex
          direction="row"
          gap={10}
          align="center"
          justify="space-evenly"
          mb="xl"
        >
          <IconCurrencyEthereum size={50} />
          <IconCurrencyBitcoin size={50} />
          <IconCoinMonero size={50} />
          <IconCurrencyLitecoin size={50} />
          <IconCurrencySolana size={50} />
          <IconCurrencyRipple size={50} />
        </Flex> */}
        <Flex
          align="center"
          justify="center"
          direction={{ base: "column", md: "row" }}
          gap={{ base: "1.5rem", md: 0 }}
        >
          <PlanBox
            style={{ borderRadius: "0.7rem 0 0 0.7rem" }}
            plan={plan1}
            interval={interval}
          />
          <PlanBox plan={plan2} interval={interval} highlight />
          <PlanBox
            style={{ borderRadius: "0 0.7rem 0.7rem 0" }}
            plan={plan3}
            interval={interval}
          />
        </Flex>
        <Paper mt="lg" ta="center">
          <Text c="dimmed">
            Plans will automatically add credits to your account every month.
            Credits bought through the subscription have a{" "}
            <strong>
              monthly validity, but are discounted compared to{" "}
              <Link to="/payment/prepaid">buying them directly</Link>
            </strong>
            . You can cancel your subscription at any time.
          </Text>
        </Paper>
        <Paper mt="lg" ta="center">
          <Text c="dimmed">
            Different image operations have different prices. Creating an AI
            image with default settings costs 30 credits, removing background
            from an image 30. Plugin operations&apos; cost may vary. Prices are
            subject to change.
          </Text>
        </Paper>
      </Stack>
      <HeroFaq />
      <Center>
        <Button
          component="a"
          size="lg"
          variant="gradient"
          gradient={{ from: "pink", to: "indigo", deg: 105 }}
          href="#start"
          mt="xl"
          mb="xl"
        >
          Take your pictures now!
        </Button>
      </Center>
    </Container>
  );
};
